<template>
    <div class="p-6 shadow-md rounded-md mb-15 flex flex-wrap justify-center">
        <div class="w-full grid md:grid-cols-2 gap-4">
            <div class="rounded-md bg-light flex items-center p-4">
                <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark"
                     :style="{
                        backgroundColor: surveyReport.scoreRatingDetails.bg_color,
                        color: surveyReport.scoreRatingDetails.text_color,
                    }"
                >
                    {{ surveyReport.avgScore }}
                </div>
                <p class="flex-1 text-xl font-bold leading-6">
                    PROPEL Score
                    <span class="block">
                    {{ surveyReport.scoreRatingDetails.label }}
                </span>
                </p>
            </div>
            <div class="rounded-md bg-light flex items-center p-4">
                <div class="flex items-center justify-center mr-4 rounded-full text-xl font-bold px-2 min-w-12 h-12 text-white"
                     :class="colorClasses"
                >
                    {{ survey.current_status }}
                </div>
                <p class="flex-1 text-xl font-bold leading-6">Status</p>
            </div>
        </div>
    </div>

    <div class="p-6 shadow-md rounded-md mb-15">
        <div class="w-full grid lg:grid-cols-2 gap-4">
            <div>
                <h3 class="font-bold text-3xl dark:text-gray-200 pr-6">Scores</h3>
                <div class="pt-6 grid md:grid-cols-2 gap-4">
                    <div class="rounded-md bg-light flex items-center p-4">
                        <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                            {{ surveyDetails.highestSection.avgScore }}
                        </div>
                        <p class="flex-1 text-xl font-bold leading-6">
                            Highest section score
                            <span class="block">{{ surveyDetails.highestSection.name }}</span>
                        </p>
                    </div>
                    <div class="rounded-md bg-light flex items-center p-4">
                        <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                            {{ surveyDetails.lowestSection.avgScore }}
                        </div>
                        <p class="flex-1 text-xl font-bold leading-6">
                            Lowest Section Score
                            <span class="block">{{ surveyDetails.lowestSection.name }}</span>
                        </p>
                    </div>
                </div>
            </div>
            <div>
                <h3 class="font-bold text-3xl dark:text-gray-200 pr-6">Key Information</h3>
                <div class="pt-6 grid md:grid-cols-2 gap-4">
                    <div class="rounded-md bg-light flex items-center p-4">
                        <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                 fill="white">
                                <path fill-rule="evenodd"
                                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                      clip-rule="evenodd"/>
                            </svg>
                        </div>
                        <p class="flex-1 text-xl font-bold leading-6">
                            Date opened: {{ survey.live_at ? new Date(survey.live_at).toLocaleString() : 'N/A' }} <br>
                            Date closed: {{ survey.closed_at ? new Date(survey.closed_at).toLocaleString() : 'N/A' }}
                        </p>
                    </div>
                    <div class="rounded-md bg-light flex items-center p-4">
                        <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                            £{{ surveyDetails.totalSpent }}
                        </div>
                        <p class="flex-1 text-xl font-bold leading-6">Total spent</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="w-full mt-10">
            <h3 class="font-bold text-3xl dark:text-gray-200 pr-6">Responses</h3>
            <div class="pt-6 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div class="rounded-md bg-light flex items-center p-4">
                    <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                        {{ surveyDetails.invited }}
                    </div>
                    <p class="flex-1 text-xl font-bold leading-6">Invited</p>
                </div>
                <div class="rounded-md bg-light flex items-center p-4">
                    <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                        {{ Math.round(surveyDetails.incompleteResponses * 100 / surveyDetails.totalResponses) }}%
                    </div>
                    <p class="flex-1 text-xl font-bold leading-6">
                        Incomplete Responses
                        <span class="block">{{ surveyDetails.incompleteResponses }}</span>
                    </p>
                </div>
                <div class="rounded-md bg-light flex items-center p-4">
                    <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                        {{ Math.round(surveyDetails.completeResponses * 100 / surveyDetails.totalResponses) }}%
                    </div>
                    <p class="flex-1 text-xl font-bold leading-6">
                        Complete Responses
                        <span class="block">{{ surveyDetails.completeResponses }}</span>
                    </p>
                </div>
                <div class="rounded-md bg-light flex items-center p-4">
                    <div class="flex items-center justify-center mr-4 rounded-full text-lg font-bold w-12 h-12 text-white bg-dark">
                        {{ Math.round(surveyDetails.notStarted * 100 / surveyDetails.totalResponses) }}%
                    </div>
                    <p class="flex-1 text-xl font-bold leading-6">
                        Not started
                        <span class="block">{{ surveyDetails.notStarted }}</span>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DashboardCard from '@/Components/DashboardCard';
import {computed} from "vue";

export default {
    name: 'SurveyDetailsDashboard',
    components: {
        DashboardCard,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
        surveyReport: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const isComplete = (respondent) => respondent.finished_at;
        const isIncomplete = (respondent) => respondent.finished_at === null && respondent.started_at;
        const isNotStarted = (respondent) => respondent.started_at === null;

        const surveyDetails = computed(() => {
            return {
                highestSection: Object.values(props.surveyReport.sections).reduce(function (prev, current) {
                    return (prev.avgScore > current.avgScore) ? prev : current;
                }),
                lowestSection: Object.values(props.surveyReport.sections).reduce(function (prev, current) {
                    return (prev.avgScore < current.avgScore) ? prev : current;
                }),
                completeResponses: Object.values(props.survey.respondents).filter(isComplete).length,
                incompleteResponses: Object.values(props.survey.respondents).filter(isIncomplete).length,
                notStarted: Object.values(props.survey.respondents).filter(isNotStarted).length,
                invited: Object.values(props.survey.respondents).filter(function (respondent) {
                    return respondent.invited === true;
                }).length,
                totalResponses: Object.values(props.survey.respondents).length,
                totalSpent: Object.values(props.survey.orders).reduce(function (prev, current) {
                    return prev.total_price + (current.status === 1 ? current.total_price : 0);
                }, 0),
            };
        });

        const pillColorsByStatus = [
            'danger', //draft 0
            'warning', //scheduled 1
            'success', //live 2
            'danger', //no respondent slots 3
            'info', //closed 4
            'success', //reopened 5
        ];

        const colorClasses = computed(() => {
            switch (pillColorsByStatus[props.survey.status]) {
                case 'success':
                    return 'bg-green-900';
                case 'warning':
                    return 'bg-orange-900';
                case 'danger':
                    return 'bg-red-900';
                default:
                    return 'text-black bg-gray-100';
            }
        });

        return {
            surveyDetails,
            pillColorsByStatus,
            colorClasses,
        };
    }
}
</script>
